import { progressBarLabel } from "./progressBarLabel";
import { progressSegments, type ReviewStatusCounts, type SegmentTone } from "./progressSegments";
import styles from "./CmsStackedBar.module.css";

type CmsStackedBarProps = {
  complete: number;
  inProgress: number;
  total: number;
  /** "large" is for a standalone summary band; the default suits an inline table column. */
  size?: "default" | "large";
  /** "review-status" colours segments by ready-for-release/editing/pending; default is unchanged. */
  variant?: "practice-area" | "review-status";
  /** Required when variant is "review-status"; ignored otherwise. */
  reviewStatusCounts?: ReviewStatusCounts;
};

export const toneClassName: Partial<Record<SegmentTone, keyof typeof styles>> = {
  complete: "barComplete",
  inProgress: "barInProgress",
  remaining: "barEmpty",
  readyForRelease: "barReadyForRelease",
  readyForEditing: "barReadyForEditing",
  pending: "barPending",
};

export function CmsStackedBar({
  complete,
  inProgress,
  total,
  size = "default",
  variant = "practice-area",
  reviewStatusCounts,
}: CmsStackedBarProps) {
  const barClassName =
    size === "large" ? `${styles.progressBar} ${styles.progressBarLarge}` : styles.progressBar;

  if (variant === "review-status" && reviewStatusCounts) {
    const segments = progressSegments({ variant: "review-status", counts: reviewStatusCounts });
    const label = progressBarLabel(reviewStatusCounts);

    return (
      <div className={barClassName} role="img" aria-label={label}>
        {segments.map(segment => (
          <div
            key={segment.tone}
            className={styles[toneClassName[segment.tone] ?? "barEmpty"]}
            style={{ flex: segment.size }}
          />
        ))}
      </div>
    );
  }

  const segments = progressSegments({
    variant: "practice-area",
    counts: { complete, inProgress, total },
  });

  return (
    <div className={barClassName}>
      {segments.map(segment => (
        <div
          key={segment.tone}
          className={styles[toneClassName[segment.tone] ?? "barEmpty"]}
          style={{ flex: segment.size }}
        />
      ))}
    </div>
  );
}
